import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MsalBroadcastService, MsalGuard, MsalInterceptor, MsalModule, MsalService } from '@azure/msal-angular';
import { QuillModule } from 'ngx-quill';
import { MobileUserInfoComponent } from '@core/components/mobile-user-info/mobile-user-info.component';
import { HeaderInterceptor } from '@core/interceptors/header-interceptor';
import { MissingTranslationHandler, TranslateModule } from '@ngx-translate/core';
import { SharedModule } from '@shared/shared.module';
import { AlertModule } from 'ngx-bootstrap/alert';
import {
  BsDatepickerConfig,
  BsDatepickerModule,
  BsDaterangepickerConfig,
  BsDaterangepickerInlineConfig
} from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { analyticsInitializer } from '../modules/analytics/analytics-initializer';
import { AnalyticsModule } from '../modules/analytics/analytics.module';
import { ModalModule } from '../modules/modal/modal.module';
import { CountryDropdownComponent } from './components/country-dropdown/country-dropdown.component';
import { ErrorComponent } from './components/error/error.component';
import { HeaderComponent } from './components/header/header.component';
import { LoaderComponent } from './components/loader/loader.component';
import { NotificationsCenterComponent } from './components/notifications-center/notifications-center.component';
import { ToastrComponent } from './components/toastr/toastr.component';
import { UrgentAlertsComponent } from './components/urgent-alerts/urgent-alerts.component';
import { UserInfoComponent } from './components/user-info/user-info.component';
import { DateConfig } from './config/date.config';
import { MsalConfiguration, MsalGuardConfig, MsalInterceptorConfig } from './config/msal.config';
import { AdminGuard } from './guards/admin.guard';
import { CacheInterceptor } from './interceptors/cache.interceptor';
import { HttpErrorInterceptor } from './interceptors/http-error.interceptor';
import { HttpLoaderInterceptor } from './interceptors/http-loader.interceptor';
import { HttpLostLocalstorageInterceptor } from './interceptors/http-lost-localstorage.interceptor';
import { JsonDateInterceptor } from './interceptors/json-date.interceptor';
import { AccountService } from './services/account.service';
import { resolveTranslationFactory } from './translation/resolve-translation';
import { UrgentAlertsSliderComponent } from './components/urgent-alerts/components/urgent-alerts-slider/urgent-alerts-slider.component';

@NgModule({
  declarations: [
    ErrorComponent,
    ToastrComponent,
    LoaderComponent,
    HeaderComponent,
    NotificationsCenterComponent,
    UserInfoComponent,
    MobileUserInfoComponent,
    CountryDropdownComponent,
    UrgentAlertsComponent,
    UrgentAlertsSliderComponent
  ],
  exports: [ToastrComponent, LoaderComponent, HeaderComponent],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    SharedModule,
    MsalModule.forRoot(MsalConfiguration, MsalGuardConfig, MsalInterceptorConfig),
    QuillModule.forRoot(),
    AnalyticsModule.forRoot(),
    TooltipModule,
    BsDropdownModule,
    AlertModule,
    BsDatepickerModule,
    PaginationModule,
    ModalModule,
    TypeaheadModule,
    TranslateModule.forRoot({
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useFactory: resolveTranslationFactory,
        deps: [HttpClient]
      }
    })
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    AdminGuard,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class CoreModule {
  public static forRoot() {
    return {
      ngModule: CoreModule,
      providers: [
        { provide: HTTP_INTERCEPTORS, useClass: HttpLostLocalstorageInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpLoaderInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: JsonDateInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HeaderInterceptor, multi: true },

        {
          provide: BsDaterangepickerInlineConfig,
          useFactory: DateConfig.BsDaterangepickerInlineConfig
        },
        {
          provide: BsDatepickerConfig,
          useFactory: DateConfig.BsDatepickerConfig
        },
        {
          provide: BsDaterangepickerConfig,
          useFactory: DateConfig.BsDaterangepickerConfig
        },
        {
          provide: APP_INITIALIZER,
          useFactory: initFactory,
          deps: [AccountService],
          multi: true
        },
        { provide: APP_INITIALIZER, useValue: analyticsInitializer, multi: true }
      ]
    };
  }

  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error('CoreModule has already been loaded. You should only import Core modules in the AppModule only.');
    }
  }
}

export function initFactory(accountService: AccountService): any {
  return async () => {
    await accountService.init();
  };
}
